import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as Y from "yjs";
import type { HocuspocusProvider } from "@hocuspocus/provider";
import { useAppSelector } from "@/state/hooks";
import { documentSessionManager } from "@/components/aiBridge/documentSessionManagerSingleton";
import type { RoomParts } from "./roomName";
import type { TrustedPresenceBinding } from "./trustedPresenceBindings";

export interface CollabUser {
    userid: number;
    name: string;
    color: string;
}

export interface CollabSession {
    ydoc: Y.Doc;
    provider: HocuspocusProvider;
    status: "connecting" | "connected" | "offline";
    synced: boolean;
    /** True once the provider has completed its first sync for this room; never
     * reset within a session — used to keep the collab binding through transient
     * disconnects. */
    everSynced: boolean;
    roomName: string;
    trustedBindings: ReadonlyMap<number, TrustedPresenceBinding>;
}

const CollabSessionContext = createContext<CollabSession | null>(null);

/** Null when collab is disabled, not configured, unauthorized, or no doc is open. */
export function useCollabSession(): CollabSession | null {
    return useContext(CollabSessionContext);
}

export const CollabSessionProvider: React.FC<{ children: React.ReactNode; roomPartsOverride?: RoomParts | null }> = ({ children, roomPartsOverride }) => {
    const { status: docsStatus, activeVersion, activeContext } = useAppSelector(state => state.workProductDocuments);

    const reduxRoomParts = useMemo<RoomParts | null>(() => {
        if (docsStatus !== "succeeded" || !activeContext || activeVersion == null) return null;
        return {
            projectId: activeContext.projectId,
            featureId: activeContext.featureId,
            userStoryId: activeContext.userStoryId,
            workProductId: activeContext.workProductId,
            version: activeVersion
        };
    }, [docsStatus, activeContext, activeVersion]);
    const roomParts = roomPartsOverride !== undefined ? roomPartsOverride : reduxRoomParts;

    const [session, setSession] = useState<CollabSession | null>(null);

    useEffect(() => {
        if (!roomParts) {
            setSession(null);
            return;
        }
        const handle = documentSessionManager.acquireView(roomParts);
        if (!handle) {
            setSession(null); // collab disabled/unconfigured
            return;
        }
        const publish = () =>
            setSession({
                ydoc: handle.session.ydoc,
                // Production factory always returns a real HocuspocusProvider.
                provider: handle.session.provider as unknown as HocuspocusProvider,
                status: handle.session.status,
                synced: handle.session.synced,
                everSynced: handle.session.everSynced,
                roomName: handle.session.roomName,
                trustedBindings: handle.session.trustedBindings
            });
        publish();
        const unsubscribe = handle.subscribe(publish);
        // If the manager destroys the underlying session out from under us (e.g.
        // auth failure), its listeners are cleared without firing, so `publish`
        // never runs again. Without this, the context would keep serving a
        // stale session whose ydoc/provider have been destroyed. Fall back to
        // null so consumers drop back to non-collab editing, matching the old
        // onAuthenticationFailed -> setSession(null) behavior.
        const unsubscribeDestroyed = documentSessionManager.onSessionDestroyed(roomName => {
            if (roomName === handle.session.roomName) setSession(null);
        });
        return () => {
            unsubscribeDestroyed();
            unsubscribe();
            handle.release();
            setSession(null);
        };
    }, [roomParts]);

    return <CollabSessionContext.Provider value={session}>{children}</CollabSessionContext.Provider>;
};
